import type { UserLocalStorage } from '../../types/localStorage';

import { use, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { getTestProps } from '../../lib/helpers';

import useDarkMode from '../../hooks/useDarkMode';

import UserContext from '../../contexts/UserContext';

import {
  BrandheroSadDark,
  BrandheroSadLight,
  BrandheroWorkspaceDark,
  BrandheroWorkspaceLight,
} from '../../images/shapes';

interface DowngradePlanModalContentProps {
  /**
   * Downgrade reason
   */
  type: 'downgraded' | 'unassigned';
  /**
   * Component test id
   */
  testId?: string;
}

const DowngradePlanModalContent = ({
  type,
  testId = '',
}: DowngradePlanModalContentProps) => {
  const { t } = useTranslation();
  const [darkMode] = useDarkMode();
  const { isAdmin } = use(UserContext);
  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');

  const BrandheroComponent = useMemo(() => {
    if (type === 'downgraded')
      return darkMode ? BrandheroWorkspaceDark : BrandheroWorkspaceLight;
    return darkMode ? BrandheroSadDark : BrandheroSadLight;
  }, [darkMode, type]);

  const transKey = type === 'downgraded' ? 'Ended' : 'SeatLimit';

  return (
    <div
      className="flex flex-col items-center justify-center text-lg text-center"
      {...getTestProps(testId)}
    >
      <BrandheroComponent
        className={twMerge('w-36 mb-4', type === 'downgraded' && 'mt-9')}
      />
      <h2 className="text-3xl mb-4" {...getTestProps(testId, 'title')}>
        {t(`Trial.${transKey}.Title`)}
      </h2>
      <p
        className="whitespace-pre-line"
        {...getTestProps(testId, 'description')}
      >
        {t(`Trial.${transKey}.Description`)}
      </p>
      <br />
      <p {...getTestProps(testId, 'upgrade')}>
        {isAdmin ? (
          <Trans
            i18nKey={`Trial.${transKey}.UpgradePlan`}
            components={{
              1: <strong />,
            }}
          />
        ) : (
          <Trans
            i18nKey={`Trial.${transKey}.ContactAdmin`}
            components={{
              1: <strong />,
            }}
            values={{
              admin: user?.data?.organization_admins?.[0],
            }}
          />
        )}
      </p>
    </div>
  );
};

export default DowngradePlanModalContent;
